<template>
    <div>
        <div v-if="!noTranscript && !pendingTranscript" ref="transcriptContainer"
            :class="['transcript-container', { 'compact-mode': compact }]">
            <div v-for="(segment, index) in transcript" :key="index"
                :ref="`segment-${index}`"
                :class="['transcriptSegment', { 'active-segment': index === activeSegmentIndex }]"
                :title="compact ? formatTimecode(segment.s) : ''"
                @click="segmentClicked(segment)">
                <span class="segment-text">{{ segment.t }}</span>
                <span v-if="!compact" class="segment-timecode">{{ formatTimecode(segment.s) }}</span>
            </div>
        </div>
        <div v-if="pendingTranscript">
            <h6>Transcript processing. Please try again in a few minutes.</h6>
        </div>
        <div v-if="noTranscript">
            <h6>Transcript not available.</h6>
            <b-button variant="primary" @click="requestTranscript">Request Transcript</b-button>
        </div>
    </div>
</template>

<script lang="ts">
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Axios from "axios";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import moment from "moment";

@Component({
    name: "TranscriptionViewer",
})
export default class TranscriptionViewer extends Vue {
    @Prop() private entityType!: number;
    @Prop() private entityId!: number | string;
    @Prop() private currentTime!: number | null;
    @Prop({ default: false }) private compact!: boolean;
    private transcript?: Array<any> = [];
    private pendingTranscript: boolean = false;
    private noTranscript: boolean = false;

    get normalizedEntityId(): number {
        return typeof this.entityId === 'string' ? parseInt(this.entityId, 10) : this.entityId;
    }

    get activeSegmentIndex(): number {
        if (this.currentTime == null || !this.transcript || this.transcript.length === 0) {
            return -1;
        }

        return this.transcript.findIndex(segment => {
            const start = moment.duration(segment.s).asSeconds();
            const end = moment.duration(segment.e).asSeconds();
            return this.currentTime! >= start && this.currentTime! < end;
        });
    }

    mounted() {
        this.fetchTranscript();
    }

    fetchTranscript() {
        this.pendingTranscript = false;
        this.noTranscript = false;
        Axios.get(`Transcript/${this.entityType}/${this.normalizedEntityId}`).then((response) => {
            if (response.status == 204) {
                this.pendingTranscript = true;
            }
            this.transcript = response.data;
        }).catch((error) => {
            if (error.response.status == 404) {
                this.noTranscript = true;
                return;
            }
            this.$notification(NotificationOptions.error(error));
        });
    }

    segmentClicked(segment: any) {
        this.$emit("transcriptclick", this.normalizedEntityId, segment.s);
    }

    formatTimecode(timespan: string): string {
        const duration = moment.duration(timespan);
        const minutes = Math.floor(duration.asMinutes());
        const seconds = duration.seconds();
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    @Watch('activeSegmentIndex')
    onActiveSegmentChanged(newIndex: number, oldIndex: number) {
        if (newIndex !== -1 && newIndex !== oldIndex) {
            this.$nextTick(() => {
                const segmentRef = this.$refs[`segment-${newIndex}`];
                const containerRef = this.$refs['transcriptContainer'];

                if (segmentRef && Array.isArray(segmentRef) && segmentRef[0] && containerRef) {
                    const segment = segmentRef[0] as HTMLElement;
                    const container = containerRef as HTMLElement;

                    // Calculate the position to scroll to center the segment
                    const segmentTop = segment.offsetTop;
                    const segmentHeight = segment.offsetHeight;
                    const containerHeight = container.clientHeight;

                    const scrollTo = segmentTop - (containerHeight / 2) + (segmentHeight / 2);

                    container.scrollTo({
                        top: scrollTo,
                        behavior: 'smooth'
                    });
                }
            });
        }
    }

    requestTranscript() {
        Axios.post(`Transcript/${this.entityType}/${this.normalizedEntityId}`)
            .then(() => {
                this.fetchTranscript();
            }).catch((error) => this.$notification(NotificationOptions.error(error)));
    }
}
</script>

<style scoped>
.transcript-container {
    line-height: 1.6;
    max-width: 100%;
    max-height: 50vh;
    overflow-y: auto;
    transition: all 0.3s ease;
}

.transcriptSegment {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    transition: all 0.3s ease;
}

/* Compact mode styles */
.transcript-container.compact-mode {
    line-height: 1.8;
}

.compact-mode .transcriptSegment {
    display: inline;
    padding: 0;
    border-bottom: none;
    margin-right: 4px;
}

.compact-mode .segment-text {
    padding-right: 0;
}

.segment-text {
    flex: 1;
    padding-right: 16px;
}

.segment-timecode {
    flex-shrink: 0;
    color: #999;
    font-size: 0.875em;
    white-space: nowrap;
    align-self: flex-start;
}

.transcriptSegment:hover {
    color: #0000FF;
    background-color: #f0f0f0;
    cursor: pointer;
    padding-left: 8px;
    border-left: 3px solid #0000FF;
}

.transcriptSegment:hover .segment-timecode {
    color: #666;
}

.transcriptSegment:last-child {
    border-bottom: none;
}

.active-segment {
    background-color: #FFF8DC;
    font-weight: 600;
    padding-left: 8px;
    border-left: 4px solid #FFD700;
    color: #000;
}

.active-segment .segment-timecode {
    color: #888;
    font-weight: 500;
}

.active-segment:hover {
    background-color: #FFECB3;
    border-left: 4px solid #FFA500;
}

/* Compact mode hover and active styles */
.compact-mode .transcriptSegment:hover {
    background-color: #f0f0f0;
    border-left: none;
    padding-left: 0;
    padding: 2px 4px;
    border-radius: 3px;
}

.compact-mode .active-segment {
    background-color: #FFF8DC;
    border-left: none;
    padding-left: 0;
    padding: 2px 4px;
    border-radius: 3px;
    border-bottom: 2px solid #FFD700;
}

.compact-mode .active-segment:hover {
    background-color: #FFECB3;
    border-left: none;
    border-bottom: 2px solid #FFA500;
}
</style>
